import {Color} from '@benchling/nucleus-ui';
import {Kbd, TextInput, UnstyledButton, createStyles} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const shortcutKeys = isMac ? ['⌘', '/'] : ['Ctrl', '/'];

const CONTAINER_HEIGHT = 49;
const INPUT_HEIGHT = 29;
const PILL_EDGE_PADDING = 8;
const PILL_GAP = 8;
const PILL_RADIUS = 30;
const ICON_SIZE = 14;
const HINT_CHIP_WIDTH = 35;
const ICON_WIDTH = PILL_EDGE_PADDING + ICON_SIZE + PILL_GAP;
const HINT_WIDTH = PILL_GAP + HINT_CHIP_WIDTH + PILL_EDGE_PADDING;
// Reserved width for the "Start AI chat" pill (icon + label + padding, ~93px per Figma). When the
// pill is shown it sits left of the hint chip, so the right section widens to fit both.
const AI_PILL_WIDTH = 95;
// Tight gap between the "Start AI chat" pill and the ⌘/ hint chip per Figma (narrower than PILL_GAP).
const AI_PILL_HINT_GAP = 4;
const AI_HINT_WIDTH = PILL_GAP + AI_PILL_WIDTH + AI_PILL_HINT_GAP + HINT_CHIP_WIDTH + PILL_EDGE_PADDING;

const useStyles = createStyles(
  (theme, {showHint, showAiButton}: {showHint: boolean; showAiButton: boolean}) => ({
    root: {
      boxSizing: 'border-box' as const,
      width: '100%',
      height: CONTAINER_HEIGHT,
      // Fixed-height row inside the omnibox's flex column: never shrink when results fill the modal
      // to its max-height, or the centered input gets squeezed upward as results land.
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${theme.spacing.sm} ${theme.spacing.lg} ${theme.spacing.xs}`,
      // Top section of the omnibox frame: top + side borders with the popup's 12px top radius, per Figma.
      borderWidth: '1px 1px 0px 1px',
      borderStyle: 'solid',
      borderColor: Color.GRAY_400,
      borderRadius: '12px 12px 0px 0px',
    },
    wrapper: {
      flex: 1,
    },
    input: {
      height: INPUT_HEIGHT,
      minHeight: INPUT_HEIGHT,
      border: `1px solid ${theme.colors.gray[3]}`,
      borderRadius: PILL_RADIUS,
      backgroundColor: theme.white,
      paddingTop: theme.spacing.xs,
      paddingBottom: theme.spacing.xs,
      // Mantine v6 rem()-wraps both `iconWidth` and `rightSectionWidth` (see Input.js) assuming a 16px root,
      // but this app's root is 10px, so those insets render at 62.5% of their px value. Pin the horizontal
      // insets here in px to bypass that rem shrinkage. `&[data-with-...]` matches the specificity of
      // Mantine's own rules so our px value wins; `!important` beats Mantine's inline padding-right.
      '&[data-with-icon]': {
        paddingLeft: ICON_WIDTH,
      },
      paddingRight: `${showHint ? (showAiButton ? AI_HINT_WIDTH : HINT_WIDTH) : PILL_EDGE_PADDING}px !important`,
      // 13px search text per Figma, matching the italic placeholder below.
      fontSize: theme.fontSizes.md,
      // Mantine's Input defaults the value's line-height to `calc(size - 2px)` (~21px here), which
      // overflows the 19px content box and makes typed text sit slightly off-center. Pin it to the
      // 14px search-text line-height from Figma so the value fits the box and centers like the
      // placeholder below.
      lineHeight: '14px',
      '&::placeholder': {
        fontFamily: theme.fontFamily,
        fontWeight: 400,
        fontStyle: 'italic',
        fontSize: theme.fontSizes.md,
        // 14px search-text line-height per Figma, matching the typed value above.
        lineHeight: '14px',
        letterSpacing: 0,
        // Pin the placeholder to GRAY_500 (#979799) per Figma rather than relying on Mantine's default.
        color: theme.colors.gray[5],
      },
    },
    icon: {
      color: theme.colors.gray[6],
      display: 'flex',
      alignItems: 'center',
      width: ICON_WIDTH,
      justifyContent: 'flex-start',
      paddingLeft: PILL_EDGE_PADDING,
    },
    searchIcon: {
      fontSize: ICON_SIZE,
      width: ICON_SIZE,
      height: ICON_SIZE,
      display: 'block',
    },
    rightSection: {
      width: showAiButton ? AI_HINT_WIDTH : HINT_WIDTH,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: AI_PILL_HINT_GAP,
      paddingRight: PILL_EDGE_PADDING,
    },
    // "Start AI chat" pill: light-grape chip with a sparkle icon + grape label (GRAPE_150 bg,
    // GRAPE_400 text/icon per Figma), sitting inside the search bar left of the shortcut hint.
    aiPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing.xs,
      flexShrink: 0,
      height: 20.5,
      padding: `${theme.spacing.xxs} ${theme.spacing.sm}`,
      borderRadius: 14,
      fontFamily: theme.fontFamily,
      fontWeight: 400,
      fontSize: theme.fontSizes.sm,
      lineHeight: '16.5px',
      whiteSpace: 'nowrap',
      // GRAPE_400 text/icon on a GRAPE_150 chip per Figma (grape[6] / grape[2] in the theme scale).
      color: theme.colors.grape[6],
      backgroundColor: theme.colors.grape[2],
    },
    aiPillIcon: {
      fontSize: 10,
      width: 10,
      height: 10,
      display: 'block',
    },
    kbd: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Tight gap between the ⌘ and / keys per Figma.
      gap: 1,
      width: HINT_CHIP_WIDTH,
      height: 20.5,
      borderRadius: 14,
      padding: '2px 6px 2px 7px',
      fontFamily: theme.fontFamily,
      fontWeight: 400,
      fontSize: 12,
      lineHeight: '16.5px',
      letterSpacing: 0,
      color: theme.colors.gray[6],
      whiteSpace: 'nowrap',
      // Flat white chip per Figma. Mantine's Kbd also adds a border, a heavier 3px bottom "keycap"
      // lip, and a white text "highlight" shadow; drop those so it reads as a borderless flat pill.
      backgroundColor: theme.white,
      border: 'none',
      boxShadow: 'none',
      textShadow: 'none',
    },
  })
);

interface QuickSearchInputProps {
  query: string;
  onQueryChange: (value: string) => void;
  placeholder: string;
  inputRef: React.RefObject<HTMLInputElement>;
  listboxId?: string;
  activeDescendantId?: string;
  expanded?: boolean;
  // When true (and the query is empty), a "Start AI chat" pill is shown in the search bar.
  showAiChatButton?: boolean;
  onStartAiChat?: () => void;
}

export default function QuickSearchInput({
  query,
  onQueryChange,
  placeholder,
  inputRef,
  listboxId,
  activeDescendantId,
  expanded,
  showAiChatButton = false,
  onStartAiChat,
}: QuickSearchInputProps): JSX.Element {
  const showHint = !query;
  // The pill shares the empty-query gate with the shortcut hint, so it hides as soon as the user types.
  const showAiButton = showAiChatButton && showHint;
  const {classes} = useStyles({showHint, showAiButton});
  return (
    <TextInput
      ref={inputRef}
      classNames={{
        root: classes.root,
        wrapper: classes.wrapper,
        input: classes.input,
        icon: classes.icon,
        rightSection: classes.rightSection,
      }}
      value={query}
      onChange={(e) => {
        onQueryChange(e.currentTarget.value);
      }}
      placeholder={placeholder}
      icon={<BenchlingIcon icon="search" className={classes.searchIcon} />}
      iconWidth={ICON_WIDTH}
      // Passing `undefined` when the hint is hidden avoids rendering an empty right-section box.
      rightSection={
        showHint ? (
          <>
            {showAiButton && (
              <UnstyledButton
                type="button"
                className={classes.aiPill}
                // Prevent the input from stealing focus (and the click being swallowed) on mousedown;
                // the click handler runs on click and closes the omnibox anyway.
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={onStartAiChat}
              >
                <BenchlingIcon icon="sparkle" className={classes.aiPillIcon} aria-hidden="true" />
                Start AI chat
              </UnstyledButton>
            )}
            <Kbd className={classes.kbd}>
              {shortcutKeys.map((key) => (
                <span key={key}>{key}</span>
              ))}
            </Kbd>
          </>
        ) : undefined
      }
      rightSectionWidth={showHint ? (showAiButton ? AI_HINT_WIDTH : HINT_WIDTH) : PILL_EDGE_PADDING}
      role="combobox"
      aria-label="Search, navigate, or ask a question"
      aria-autocomplete="list"
      aria-expanded={expanded ?? false}
      aria-controls={listboxId}
      aria-activedescendant={activeDescendantId}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={true}
    />
  );
}
